function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

function callOnLoad(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let wait = false;
  return (...args) => {
    if (wait) return;

    fn(...args);
    wait = true;

    setTimeout(() => {
      wait = false;
    }, delay);
  };
}

// TODO: Test with rest parameters
function once(fn) {
  let ran = false;
  let result;
  return function (...args) {
    if (ran) return result;
    result = fn.apply(this, args);
    ran = true;
    return result;
  };
}

// TODO: Test with rest parameters
function memoize(fn) {
  const cache = new Map();
  return function (...args) {
    const key = JSON.stringify(args);
    if (cache.has(key)) return cache.get(key);

    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };
}

function curry(fn, arity = fn.length) {
  return function curried(...args) {
    if (args.length >= arity) return fn(...args);
    return function (...moreArgs) {
      return curried(...args, ...moreArgs);
    };
  };
}

// TODO: Test using quokka
function partial(fn, ...args) {
  return (..._args) => fn(...args, ..._args);
}

function pipe(...fns) {
  return function piped(...args) {
    return fns.reduce((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

function compose(...fns) {
  return function composed(...args) {
    return fns.reduceRight((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

function pick(obj, keys) {
  return keys.reduce((acc, key) => {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

function omit(obj, keys) {
  return Object.keys(obj)
    .filter(key => !keys.includes(key))
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
}

function zip(...arrays) {
  const maxLength = Math.max(...arrays.map(array => array.length));
  return Array.from({ length: maxLength }).map((_, i) => {
    return Array.from({ length: arrays.length }, (_, j) => arrays[j][i]);
  });
}

function range(min, max, step = 1) {
  if (max === undefined) {
    max = min;
    min = step;
  }

  return Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => min + i * step);
}

// Reinject all script tags within an AJAX-loaded element to allow script execution. By default, scripts are disabled when using element.innerHTML.
function reinjectScripts(element) {
  console.debug('Reinjecting script tags for element:', element);

  element.querySelectorAll('script').forEach(oldScriptTag => {
    const newScriptTag = document.createElement('script');
    Array.from(oldScriptTag.attributes).forEach(attribute => {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
  });
}

function camelToKebab(str) {
  if (str != str.toLowerCase()) {
    str = str.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
  }

  return str;
}

function kebabToCamel(str) {
  return str.replace(/-./g, m => m[1].toUpperCase());
}

function parseAttribute(value) {
  if (VALUE_PATTERNS.boolean.test(value)) {
    return value === 'true';
  }

  if (VALUE_PATTERNS.number.test(value)) {
    return Number(value);
  }

  return value;
}

function parseBooleanAttribute(value) {
  return value !== null && value !== 'false';
}

function smoothScroll(target, {
  container = window,
  axis = 'y',
  offset = 0,
  targetBounds = null,
  alignToClosestEdge = false,
  jump = false,
  onComplete = () => {},
} = {}, {
  distanceMax = 5000,
  divisor = distanceMax * 1.15,
  distanceMin = divisor * 0.3,
  ease = 'sine.out',
} = {}) {
  const containerScrollProp =
    container === window
      ? (axis === 'y') ? 'scrollY' : 'scrollX'
      : (axis === 'y') ? 'scrollTop' : 'scrollLeft';
  const containerSizeProp =
    container === window
      ? (axis === 'y') ? 'innerHeight' : 'innerWidth'
      : (axis === 'y') ? 'offsetHeight' : 'offsetWidth';

  const start = container[containerScrollProp];

  // TODO: Rename `target` arg to `end`
  let end = target
  if (end instanceof HTMLElement) {
    targetBounds ??= end.getBoundingClientRect();
    end = start + targetBounds[axis];

    if (alignToClosestEdge && targetBounds[axis] > (container[containerSizeProp] / 2)) {
      end -= (container[containerSizeProp] - targetBounds[(axis === 'y' ? 'height' : 'width')]);
      offset *= -1;
    }
  }

  const duration = jump ? 0 : calculateScrollDuration(start, end - offset, {
    distanceMax,
    divisor,
    distanceMin,
    ease,
  });

  document.body.classList.add('is-scrolling');
  document.querySelector('sticky-header')?.dispatchEvent(new Event('preventHeaderReveal'));
  gsap.to(container, {
    duration,
    ease,
    scrollTo: {
      [axis]: end,
      [`offset${axis.toUpperCase()}`]: offset,
    },
    onComplete: () => {
      document.body.classList.remove('is-scrolling');
      onComplete();
    },
  });
}

function calculateScrollDuration(start, end, {
  distanceMax = 5000,
  divisor = distanceMax * 1.15,
  distanceMin = divisor * 0.3,
  ease = 'sine.out',
} = {}) {
  const rawDistance = Math.abs(end - start);

  // Add easing to calculate more natural duration values
  const distance = ease
    ? gsap.utils.pipe(
        gsap.utils.mapRange(0, distanceMax, 0, 1),
        gsap.parseEase(ease),
        gsap.utils.mapRange(0, 1, 0, distanceMax)
      )(rawDistance)
    : rawDistance;

  // Calculate scroll duration based on distance to target
  // Clamp duration to prevent scrolling too fast or slow
  const duration = clamp(distanceMin, distance, distanceMax) / divisor;

  return duration;
}

function isIterable(value) {
  return value != null && value != undefined && typeof value[Symbol.iterator] === 'function';
}

function isInViewport(element, {
  bounds = element.getBoundingClientRect(),
  partial = false
} = {}) {
  const { top, left, bottom, right, height, width } = bounds;
  const { innerHeight, innerWidth } = window;


  if (!partial) {
    return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
  }
  
  const minVisibleHeight = partial === true ? 1 : height * partial;
  const minVisibleWidth = partial === true ? 1 : width * partial;
  const maxVerticalOffset = innerHeight - (minVisibleHeight - 1);
  const maxHorizontalOffset = innerWidth - (minVisibleWidth - 1);

  // TODO: TEST THOROUGHLY
  return (
    ((top > (minVisibleHeight - height) && top < maxVerticalOffset) || (bottom > minVisibleHeight && bottom < maxVerticalOffset)) &&
    ((left > (minVisibleWidth - width) && left < maxHorizontalOffset) || (right > minVisibleWidth && right < maxHorizontalOffset))
  );
  // return (
  //   ((top > 0 && top < maxVerticalOffset) || (bottom > 0 && bottom < maxVerticalOffset)) &&
  //   ((left > 0 && left < maxHorizontalOffset) || (right > 0 && right < maxHorizontalOffset))
  // );

  // return partial
  //   ? ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
  //       ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
  //   : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
}

function toggleStateWithTimeout(enabledCallback, disabledCallback, timeoutDuration = 1000) {
  let timeout;
  const enableState = () => {
    enabledCallback();
    timeout = setTimeout(disableState, timeoutDuration);
  }

  const disableState = () => {
    disabledCallback();
    clearTimeout(timeout);
  }

  const clearStateTimeout = () => {
    clearTimeout(timeout);
    timeout = undefined;
  }

  return {
    enable: enableState,
    disable: disableState,
    clear: clearStateTimeout,
  }
}